import { resolveInsulationVolume } from '../../utils/insulationVolume.js';
import { isInsulationOption } from '../../utils/insulationCatalog.js';

/**
 * Ввод количества утеплителя в упаковках, м² или м³ — на уровне расчёта.
 *
 * В подборе потребность уже вводится так, и менеджеры попросили то же самое в
 * самой таблице товаров: объект считают в квадратах, а прайс — в упаковках, и
 * пересчёт в уме на каждой строке ошибается чаще, чем кажется.
 *
 * Правило одно и то же с подбором: упаковка целая, поэтому нужное количество
 * округляется ВВЕРХ. Просят 1000 м² — уходит 1009, и узнать об этом менеджер
 * должен до отправки КП.
 *
 * В сам расчёт всегда уходят УПАКОВКИ. На количество завязаны цена, масса,
 * объём и доставка, и подменять его квадратами нельзя: это сломало бы всё, что
 * считает от количества. Единица и введённое число — только помощь при вводе.
 */

/** В чём можно задать количество. Значения те же, что в подборе. */
export const QUANTITY_UNITS = { PACK: 'pack', AREA: 'm2', VOLUME: 'm3' };

export const QUANTITY_UNIT_LABELS = {
  [QUANTITY_UNITS.PACK]: 'упак',
  [QUANTITY_UNITS.AREA]: 'м²',
  [QUANTITY_UNITS.VOLUME]: 'м³',
};

const toPositive = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Сколько объёма и площади в одной упаковке.
 *
 * Спорный объём мерой не считается — ровно как в подборе. Там это правило
 * появилось после «Кнауф Инсулейшн защита от шума», у которого площадь в
 * названии завышена в полтора раза: пересчёт по такой мере дал бы недобор
 * материала на объект, а это хуже, чем отсутствие пересчёта.
 *
 * @returns {{volume: number, area: number}} нули означают «этой мерой нельзя».
 */
export const packMetrics = (name, data) => {
  if (!data || !isInsulationOption(name, data)) return { volume: 0, area: 0 };

  const { volume, isDisputed } = resolveInsulationVolume(name, data.VOLUME_CUBIC);
  if (!(volume > 0) || isDisputed) return { volume: 0, area: 0 };

  const thicknessMm = toPositive(data.INSULATION_THICKNESS);
  const area = thicknessMm ? volume / (thicknessMm / 1000) : 0;

  return { volume, area };
};

/** Единицы, которые есть смысл предлагать по этой позиции. */
export const availableUnits = (metrics) => {
  const units = [QUANTITY_UNITS.PACK];
  if (metrics && metrics.area > 0) units.push(QUANTITY_UNITS.AREA);
  if (metrics && metrics.volume > 0) units.push(QUANTITY_UNITS.VOLUME);
  return units;
};

/** Переключатель показываем, только когда есть между чем переключаться. */
export const canChooseUnit = (name, data) => availableUnits(packMetrics(name, data)).length > 1;

const perPackage = (metrics, unit) => {
  if (unit === QUANTITY_UNITS.PACK) return 1;
  if (unit === QUANTITY_UNITS.VOLUME) return metrics.volume > 0 ? metrics.volume : 0;
  return metrics.area > 0 ? metrics.area : 0;
};

/**
 * Сколько упаковок закрывает введённое количество.
 *
 * @returns null — пересчитать нечем: либо ничего не введено, либо у позиции нет
 *          этой меры. Звать с `null` на выходе значит «оставить как есть», а не
 *          «поставить ноль»: обнулённое количество стирает строку заказа.
 */
export const packagesFor = (value, unit, metrics) => {
  const requested = toPositive(value);
  if (!requested) return null;

  const per = perPackage(metrics || { volume: 0, area: 0 }, unit);
  if (!per) return null;

  // Ровно кратное количество не должно давать лишнюю упаковку. Площадь
  // считается делением (0,288 / 0,1 = 2,8799999999999994), и «2,88 м²» без
  // поправки давало бы две упаковки вместо одной. Допуск микроскопический —
  // он гасит двоичную погрешность, но не реальный перебор на грамм.
  return Math.max(1, Math.ceil(requested / per - 1e-9));
};

/**
 * Подпись под полем: что ввели и во что это превратилось.
 *
 * Показывается только когда пересчёт был, то есть не в упаковках. В упаковках
 * подпись повторяла бы поле слово в слово.
 */
export const conversionHint = (value, unit, metrics) => {
  if (unit === QUANTITY_UNITS.PACK) return null;

  const packages = packagesFor(value, unit, metrics);
  if (!packages) return null;

  const requested = toPositive(value);
  const delivered = packages * perPackage(metrics, unit);
  const overshoot = delivered - requested;
  const round = (n) => Math.round(n * 100) / 100;

  return {
    packages,
    delivered: round(delivered),
    overshoot: round(overshoot),
    text: overshoot > 0.005
      ? `${round(requested)} ${QUANTITY_UNIT_LABELS[unit]} → ${packages} упак (${round(delivered)} ${QUANTITY_UNIT_LABELS[unit]}, лишних ${round(overshoot)})`
      : `${round(requested)} ${QUANTITY_UNIT_LABELS[unit]} → ${packages} упак`,
  };
};

export default packagesFor;
